import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export type StatusTone =
  | "default"
  | "secondary"
  | "destructive"
  | "outline"
  | "success"
  | "warning"
  | "danger"
  | "info"
  | "blue"
  | "muted";

export function StatusPill({
  value,
  map,
  labels,
  fallback = "muted",
  className,
  icon,
  suffix,
}: {
  value: string;
  map?: Record<string, StatusTone>;
  labels?: Record<string, string>;
  fallback?: StatusTone;
  className?: string;
  icon?: ReactNode;
  suffix?: ReactNode;
}) {
  const tone = map?.[value] ?? fallback;
  const label = labels?.[value] ?? value;
  return (
    <Badge
      variant={tone}
      className={cn("whitespace-nowrap font-medium", (icon || suffix) && "gap-1", className)}
    >
      {icon}
      {label}
      {suffix}
    </Badge>
  );
}

export const APPLICATION_STATUS_TONES: Record<string, StatusTone> = {
  Merged: "muted",
  Draft: "muted",
  Saved: "muted",
  New: "blue",
  Submitted: "blue",
  AwaitingAgentPricing: "warning",
  Pending: "blue",
  Underwriting: "blue",
  ESignSent: "blue",
  ESigned: "blue",
  ActionNeeded: "danger",
  Approved: "success",
  Boarded: "success",
  Live: "success",
  Declined: "danger",
  Cancelled: "muted",
};
export const APPLICATION_STATUS_LABELS: Record<string, string> = {
  Merged: "Merged",
  New: "Submitted",
  AwaitingAgentPricing: "Needs pricing",
  ESignSent: "E-sign sent",
  ESigned: "E-signed",
  ActionNeeded: "Pended",
};

export const TICKET_STATUS_TONES: Record<string, StatusTone> = {
  Open: "blue",
  InProgress: "warning",
  Pending: "warning",
  Resolved: "success",
  Closed: "muted",
};
export const TICKET_STATUS_LABELS: Record<string, string> = {
  InProgress: "In Progress",
};
export const TICKET_PRIORITY_TONES: Record<string, StatusTone> = {
  Low: "blue",
  Normal: "warning",
  High: "danger",
  Urgent: "danger",
};

export const MERCHANT_STATUS_TONES: Record<string, StatusTone> = {
  Live: "success",
  Approved: "blue",
  Inactive: "muted",
  Closed: "danger",
};

export const QUOTE_STATUS_TONES: Record<string, StatusTone> = {
  Quote: "muted",
  Draft: "muted",
  Sent: "info",
  Approved: "info",
  "Sent to Invoice": "warning",
  Invoiced: "warning",
  Paid: "success",
  Cancelled: "danger",
};

export const ROLE_TYPE_TONES: Record<string, StatusTone> = {
  System: "warning",
  User: "muted",
};

export const USER_TIER_TONES: Record<string, StatusTone> = {
  ADMIN: "warning",
  IT_SUPPORT: "info",
  USER: "muted",
};
export const USER_STATUS_TONES: Record<string, StatusTone> = {
  Active: "success",
  Inactive: "muted",
};
